import { useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { readStateKey, stateWithoutKey } from "../utils/navigation/wageBreakdownReturn";

/**
 * How the user ARRIVED on this page, read from one key of `location.state` — e.g. "from a wage breakdown"
 * — and then removed from the history entry, so a refresh (or Back to this entry) does not arrive again.
 *
 * Read ONCE, in the state initialiser, not on every render: a page that tidies its own URL on mount (Team
 * strips its deep-link params with `setSearchParams(…, { replace: true })`) replaces the entry and drops
 * `location.state` with it, so by the next render the key may be gone.
 *
 * The removal is a LAYOUT effect. It rewrites the entry from the location this render saw, so it has to
 * land before any passive effect of the same commit navigates on its own — a page's redirect (the day
 * schedule's invalid-week one) or URL tidy — or it would put that location back. Layout effects all run
 * before passive ones, so the page's own navigation, whenever it is declared, commits last and stands.
 * That relies on the router already listening to its history, which holds for the only way the state is
 * written: an in-app link to an already-mounted app. (A router mounting in the same commit as the page
 * subscribes in its own layout effect, after the page's, and would not see the strip.)
 *
 * `resetKey` re-reads the state when it changes, for a page that is not remounted between records (the
 * profile switching nurses); `null` = never.
 *
 * The removal keeps every other key of the state (`navigationSrc`, …).
 */
export const useArrivalState = <T>(key: string, parse: (value: unknown) => T | undefined, resetKey: string | null): T | undefined => {
	const location = useLocation();
	const navigate = useNavigate();
	const [arrival, setArrival] = useState(() => parse(readStateKey(location.state, key)));
	const [readFor, setReadFor] = useState(resetKey);

	// A new record on the same page: what arrived with the previous one no longer applies. Adjusted during
	// render (React's "storing information from previous renders"), so the stale value is never painted.
	let current = arrival;
	if (readFor !== resetKey) {
		current = parse(readStateKey(location.state, key));
		setReadFor(resetKey);
		setArrival(current);
	}

	const { pathname, search, hash, state } = location;
	useLayoutEffect(() => {
		if (readStateKey(state, key) === undefined) return;
		navigate({ pathname, search, hash }, { replace: true, state: stateWithoutKey(state, key) });
	}, [key, navigate, pathname, search, hash, state]);

	return current;
};
